import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';
import { reconhecidoProjetoMes } from '../lib/reconhecimento';
import { LINHA_COR, LINHA_ICON } from '../constants/linhas';
import { indicadores } from './AvaliacoesPage';

const C = { purple:'#691FB1', purpleLight:'#C6C3E7', pink:'#FF357B', blue:'#57B1DD', green:'#27AE60', amber:'#F39C12', red:'#E74C3C', txt:'#2b2b3a', soft:'#6b6b7b', border:'#ECEAF6', white:'#fff' };
const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const eur = n => (Math.round(Number(n)||0)).toLocaleString('pt-PT')+' €';
const ymStr = (y,m)=> y+'-'+String(m).padStart(2,'0');

export default function ClienteView({ voltar }){
  const anoAtual=new Date().getFullYear();
  const [ano,setAno]=useState(anoAtual);
  const [clienteId,setClienteId]=useState('');
  const [clientes,setClientes]=useState([]);
  const [projetos,setProjetos]=useState([]);
  const [bolsas,setBolsas]=useState([]);        // plano de sessões por projeto
  const [briefings,setBriefings]=useState([]);  // para o universo de pessoas
  const [entidades,setEntidades]=useState([]);  // designações fiscais dos clientes
  const [sessoes,setSessoes]=useState([]);
  const [fases,setFases]=useState([]);
  const [loading,setLoading]=useState(true);
  const [tip,setTip]=useState(null); // {x,y,label,val}
  const mesHoje = new Date().getMonth()+1;
  const [periodo,setPeriodo]=useState({de:1,ate:12});
  const [nps,setNps]=useState([]);
  const [faturas,setFaturas]=useState([]);     // o que foi mesmo faturado
  const [custosDir,setCustosDir]=useState([]); // custos lançados nos projetos
  const [npsModal,setNpsModal]=useState(false);
  const [avPart,setAvPart]=useState([]);   // avaliações dos participantes (questionário /avaliar)

  useEffect(()=>{ let on=true; (async()=>{
    setLoading(true);
    const ini=ano+'-01-01', fim=ano+'-12-31';
    const [{data:cl},{data:pr},{data:se},{data:ph},{data:nps}] = await Promise.all([
      supabase.from('clientes').select('id,nome').order('nome'),
      supabase.from('projects').select('*').eq('active',true),
      (clienteId ? (async()=>{
        let todas=[], desde=0, bloco=1000;
        while(true){
          const { data:pag } = await supabase.from('sessions').select('cliente_id,value_agreed,value_to_pay,date,source,state,professional,activity,estado_sessao,modalidade').eq('cliente_id',clienteId).gte('date',ini).lte('date',fim).order('date').range(desde,desde+bloco-1);
          if(!pag||pag.length===0) break;
          todas=todas.concat(pag);
          if(pag.length<bloco) break;
          desde+=bloco;
        }
        return { data:todas };
      })() : Promise.resolve({data:[]})),
      (async () => { let todas=[]; for(let i=0;;i++){ const { data, error } = await supabase.from('project_phases').select('project_id,state,end_date,name').order('order_num').order('id').range(i*1000,i*1000+999); if(error) return { data:todas.length?todas:null, error }; todas=todas.concat(data||[]); if(!data||data.length<1000) break; } return { data:todas, error:null }; })(),
      supabase.from('avaliacao_respostas').select('cliente_id,semestre,p1_servicos,p2_equipa,p3_comercial,p4_reporting,p5_impacto,p6_nps,p7_melhorar,respondido_em').order('semestre',{ascending:false}),
    ]);
    if(!on) return;
    setClientes(cl||[]); setProjetos(pr||[]); setSessoes((se||[]).filter(s=>['confirmed','scheduled','cancelled'].includes(s.state))); setFases(ph||[]); setNps(nps||[]);
    // plano de sessões: bolsas e valências
    const { data: bs } = await supabase.from('projeto_bolsas').select('*').order('ordem');
    const { data: vs } = await supabase.from('projeto_bolsa_valencias').select('*');
    setBolsas((bs||[]).map(b=>({ ...b, valencias:(vs||[]).filter(v=>v.bolsa_id===b.id) })));
    const { data: ents } = await supabase.from('entidades_faturacao')
      .select('cliente_id,designacao_fiscal,nif').eq('ativo', true);
    setEntidades(ents||[]);
    const { data: brf } = await supabase.from('project_briefings').select('project_id,num_employees,adesao_prevista_pct');
    setBriefings(brf||[]);

    // Faturado real e custos diretos. As faturas ligam-se pelo NOME do cliente,
    // que tem variantes ("Tabaqueira", "Tabaqueira I"), por isso comparam-se
    // nomes normalizados e conta a fatura cujo nome COMEÇA pelo do cliente —
    // sem isso, "Alsa Portugal" apanhava tudo o que tivesse "Portugal".
    const { data: fat } = await supabase.from('faturas')
      .select('cliente,valor,valor_liquido,nc_valor,numero,data_emissao,month,estado')
      .gte('data_emissao', ini).lte('data_emissao', fim);
    setFaturas(fat||[]);
    const { data: cst } = await supabase.from('custos_projeto')
      .select('cliente_id,projeto_id,categoria,descricao,valor,mes');
    setCustosDir(cst||[]);
    setLoading(false);
  })(); return ()=>{ on=false; }; },[ano,clienteId]);

  // avaliações dos participantes deste cliente (últimos 12 meses)
  useEffect(() => { let on = true; (async () => {
    if (!clienteId) { setAvPart([]); return; }
    const d = new Date(); d.setMonth(d.getMonth() - 12);
    const { data, error } = await supabase.from('avaliacao_servico_respostas').select('*').eq('cliente_id', clienteId)
      .neq('match_estado', 'ignorar').gte('created_at', d.toISOString()).order('created_at', { ascending:false }).limit(3000);
    if (on) setAvPart(error ? [] : (data || []));
  })(); return () => { on = false; }; }, [clienteId]);

  const projsCli = useMemo(()=> clienteId ? projetos.filter(p=>p.cliente_id===clienteId) : [], [projetos,clienteId]);
  const sessCli  = useMemo(()=> clienteId ? sessoes.filter(s=>s.cliente_id===clienteId) : [], [sessoes,clienteId]);
  const projIds  = useMemo(()=> new Set(projsCli.map(p=>p.id)), [projsCli]);
  const npsCli = useMemo(()=> {
    if(!clienteId) return [];
    return nps.filter(n=>n.cliente_id===clienteId)
      .map(n=>({ semestre:n.semestre, valor:n.p6_nps }))
      .sort((a,b)=> b.semestre.localeCompare(a.semestre)); // mais recente primeiro
  }, [nps,clienteId]);
  const npsUltimo = npsCli[0] || null;
  const npsAnterior = npsCli[1] || null;
  const npsClassif = (v)=> v==null?'':(v>=9?'Promotor':v>=7?'Neutro':'Detrator');
  const labelSem = (s)=>{ if(!s) return ''; const [a,ss]=s.split('-'); return (ss==='S1'?'1.º sem ':'2.º sem ')+a; };
  // avaliações completas (para as 5 escalas), mais recente primeiro
  const avalCli = useMemo(()=> {
    if(!clienteId) return [];
    return nps.filter(n=>n.cliente_id===clienteId).slice().sort((a,b)=> b.semestre.localeCompare(a.semestre));
  }, [nps,clienteId]);
  const avalUlt = avalCli[0] || null;
  const avalAnt = avalCli[1] || null;
  const ESCALAS = [
    { key:'p1_servicos', label:'Qualidade dos serviços' },
    { key:'p2_equipa', label:'Equipa que acompanha' },
    { key:'p3_comercial', label:'Resposta comercial' },
    { key:'p4_reporting', label:'Informação e reporting' },
    { key:'p5_impacto', label:'Impacto e valor' },
  ];

  // receita reconhecida por mês: projetos (fixos/único/pré-pago) + sessões (por sessão)
  const recProjMes = (ym)=> projsCli.reduce((a,p)=> a + reconhecidoProjetoMes(p, ym), 0);
  const recSessMes = (ym)=> sessCli.filter(s=>(s.date||'').slice(0,7)===ym).reduce((a,s)=>a+(s.value_agreed||0),0);

  const linhas = useMemo(()=> MESES.map((nome,i)=>{ const ym=ymStr(ano,i+1); return { mes:nome, val: recProjMes(ym)+recSessMes(ym) }; }), [projsCli,sessCli,ano]);
  const totalAno = linhas.reduce((a,l,i)=> (i+1>=periodo.de && i+1<=periodo.ate) ? a+l.val : a, 0);

  const porArea = useMemo(()=>{ const t={};
    projsCli.forEach(p=>{ let v=0; for(let i=0;i<12;i++) v+=reconhecidoProjetoMes(p,ymStr(ano,i+1)); if(v>0){ const k=p.business_area||'(sem linha)'; t[k]=(t[k]||0)+v; } });
    const sv = sessCli.reduce((a,s)=>a+(s.value_agreed||0),0); // sessões: linha do projeto-pai é difícil de imputar 1:1, agrupa em "Sessões"
    if(sv>0) t['Sessões']=(t['Sessões']||0)+sv;
    return Object.entries(t).map(([label,value])=>({label,value,color:LINHA_COR[label]||C.blue}));
  }, [projsCli,sessCli,ano]);
  const totArea = porArea.reduce((a,d)=>a+d.value,0);

  // ── CONSUMO E ENVELOPE ──
  // O plano vem das bolsas; o universo vem do briefing. Só aparece nos
  // projetos que tenham plano definido.
  const consumo = useMemo(() => {
    // Mês de referência: o que está escolhido no período (ex.: setembro), e não o mês de hoje.
    // Com um período de vários meses, vale o último; nunca depois de hoje.
    const hojeReal = new Date().toISOString().slice(0,10);
    const ymRef = `${ano}-${String(periodo.ate).padStart(2,'0')}`;
    const mesAtual = ymRef < hojeReal.slice(0,7) ? ymRef : hojeReal.slice(0,7);
    const fimRef = (() => { const [y,m] = mesAtual.split('-').map(Number); return new Date(Date.UTC(y, m, 0)).toISOString().slice(0,10); })();
    const hoje = fimRef < hojeReal ? fimRef : hojeReal;
    const NOMES_M = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
    const nomeRef = mesAtual === hojeReal.slice(0,7) ? 'este mês' : `em ${NOMES_M[Number(mesAtual.slice(5,7))-1]}`;
    return projsCli.map(p => {
      const bs = bolsas.filter(b => b.projeto_id === p.id);
      if (!bs.length) return null;

      const ini = p.contract_start || (ano+'-01-01');
      // Conta o que está COMPROMETIDO: realizadas, faltas, cancelamentos tardios
      // e também as agendadas — uma sessão marcada é custo que já está preso.
      // Os cancelamentos a tempo ficam de fora: não se pagam nem ocupam agenda.
      // (sessões anuladas — ex.: duplicados — têm state 'cancelled' mesmo com estado «realizada»)
      const conta = s => s.state !== 'cancelled'
                      && !['reagendada','falta_profissional'].includes(s.estado_sessao)
                      && !String(s.estado_sessao||'').startsWith('cancelada');
      const consumidas = sessCli.filter(s => conta(s) && (s.date||'') >= ini && (s.date||'') <= hoje
        && !(s.estado_sessao === 'agendada' && (s.date||'') < hojeReal.slice(0,7) + '-01')).length;
      // Num mês já fechado, uma sessão ainda "agendada" não aconteceu (ou falta fechá-la na WOW):
      // não conta como consumida — fica à parte, como aviso. Assim bate com o Financeiro.
      const mesFechado = mesAtual < hojeReal.slice(0,7);
      const porFechar = mesFechado ? sessCli.filter(s => s.estado_sessao === 'agendada' && (s.date||'').slice(0,7) === mesAtual).length : 0;
      const contaMes = s => conta(s) && !(mesFechado && s.estado_sessao === 'agendada');
      const doMes  = sessCli.filter(s => contaMes(s) && (s.date||'').slice(0,7) === mesAtual);
      const custoMes = doMes.reduce((a,s)=>a+(Number(s.value_to_pay)||0),0);
      // O custo médio conta só as que se pagam: as agendadas entram a zero e
      // puxavam a média para baixo.
      const pagas = doMes.filter(s => (Number(s.value_to_pay)||0) > 0);
      const agendadasMes = doMes.filter(s => s.estado_sessao === 'agendada').length;
      const meses = Math.max(1, Math.round((new Date(hoje) - new Date(ini)) / 2629800000));
      const mesesContrato = (p.contract_start && p.contract_end)
        ? Math.max(1, Math.round((new Date(p.contract_end) - new Date(p.contract_start)) / 2629800000)) : 12;

      // pré-pago: soma dos pacotes das bolsas
      const pacote = bs.reduce((a,b)=>a+(Number(b.total_pacote)||0),0);

      // custo-alvo médio e pior caso, ponderado pelas sessões de cada bolsa
      let sessAno = 0, somaMedio = 0, piorCaso = 0;
      bs.forEach(b => {
        const n = Number(b.sessoes_ano)||0;
        const custos = (b.valencias||[]).map(v=>Number(v.custo_alvo)||0).filter(x=>x>0);
        if (!n) return;
        sessAno += n;
        if (custos.length) {
          somaMedio += n * (custos.reduce((a,x)=>a+x,0)/custos.length);
          piorCaso  += n * Math.max(...custos);
        }
      });
      const alvoMedio = sessAno>0 && somaMedio>0 ? somaMedio/sessAno : null;
      const alvoPior  = sessAno>0 && piorCaso>0  ? piorCaso/sessAno  : null;

      // envelope, nas avenças
      const fixos = 0;   // os custos fixos saem da WOW, não do projeto
      const envelope = (alvoMedio && aven) ? Math.floor(Math.max(0, aven-fixos)/alvoMedio) : null;
      const envelopePior = (alvoPior && aven) ? Math.floor(Math.max(0, aven-fixos)/alvoPior) : null;

      // previsão, a partir do universo do briefing
      const brf = briefings.find(b=>b.project_id===p.id);
      const uni = Number(brf?.num_employees)||0;
      const ad  = Number(brf?.adesao_prevista_pct)||0;
      const previstas = (uni && ad && sessAno) ? Math.round(uni*(ad/100)*sessAno/mesesContrato) : null;

      const ritmo = consumidas/meses;
      const restam = pacote ? Math.max(0, pacote-consumidas) : null;
      const mesesAteEsgotar = (pacote && ritmo>0) ? Math.round(restam/ritmo) : null;
      const custoMedio = pagas.length ? custoMes/pagas.length : null;

      const custoPrevistoTmp = (previstas && alvoMedio) ? previstas*alvoMedio : null;
      const margemPrevista   = (custoPrevistoTmp!=null && Number(p.value)) ? Number(p.value)-custoPrevistoTmp : null;
      const aven = Number(p.value)||0;

      // ── RITMO: o que já foi consumido face ao que era de esperar ──
      // A mesma medida serve os dois casos. No pré-pago o risco é ficarem
      // aquém e não renovarem; na avença é passarem o que o valor paga.
      const fimC = p.contract_end ? new Date(p.contract_end) : null;
      const iniC = new Date(ini);
      const pctTempo = fimC && fimC > iniC
        ? Math.min(1, Math.max(0, (new Date(hoje) - iniC) / (fimC - iniC)))
        : Math.min(1, meses/12);
      const esperado = pacote ? pacote * pctTempo
                     : (previstas ? previstas * meses : null);
      const ritmoRel = (esperado && esperado > 0) ? consumidas / esperado : null;

      // ── LEITURA E RECOMENDAÇÃO ──
      let aviso = null;
      const custoAcimaDaAvenca = (!pacote && aven > 0 && custoMes > aven);

      // Nas avenças, a previsão é o TETO que o contrato aguenta, não uma meta.
      // Ficar abaixo é bom para a margem; o risco de baixo é a subutilização
      // levar o cliente a não renovar.
      const pctTeto = (!pacote && previstas) ? doMes.length / previstas : null;

      if (custoAcimaDaAvenca) {
        aviso = { nivel:'travar', titulo:'Acima do que a avença paga',
          texto:`O custo deste mês (${eur(custoMes)}) ultrapassa a avença de ${eur(aven)}. Travar agendamentos novos e rever o plano com o cliente.` };
      } else if (consumidas === 0 && pctTempo > 0.15) {
        aviso = { nivel:'parado', titulo:'Programa parado',
          texto:'Contrato em curso e ainda sem sessões. Perceber se a comunicação chegou às pessoas e se o acesso está a funcionar.' };
      } else if (pctTeto != null) {
        const pct = Math.round(pctTeto*100);
        const det = `${doMes.length} sessões comprometidas ${nomeRef}${agendadasMes?`, das quais ${agendadasMes} ainda por dar`:''}, para um teto de ${previstas}.`
          + (porFechar ? ` Há ainda ${porFechar} sessão(ões) desse mês como «agendadas» — falta fechá-las na WOW; não estão contadas.` : '');
        if (pctTeto > 1) {
          aviso = { nivel:'travar', titulo:'Acima do teto',
            texto:`${det} Passou o que o contrato aguenta — a partir daqui cada sessão come margem. Travar agendamentos novos.` };
        } else if (pctTeto >= 0.85) {
          aviso = { nivel:'vigiar', titulo:'No limite',
            texto:`${det} Está em ${pct}% do teto. Não há folga para o resto do mês — convém não abrir mais agenda.` };
        } else if (pctTeto >= 0.40) {
          aviso = { nivel:'ok', titulo:'Dentro do previsto',
            texto:`${det} Está em ${pct}% do teto, com folga. Nada a fazer.` };
        } else {
          aviso = { nivel:'reforcar', titulo:'Subutilizado',
            texto:`${det} Só ${pct}% do teto. A margem agradece, mas um programa pouco usado é um programa que não se renova — vale a pena reforçar a divulgação.` };
        }
      } else if (ritmoRel != null) {
        if (ritmoRel < 0.5) {
          aviso = pacote
            ? { nivel:'reforcar', titulo:'Muito abaixo do ritmo',
                texto:`Consumidas ${Math.round(ritmoRel*100)}% do que era de esperar a esta altura. A este passo o pacote não se esgota até ao fim do contrato — e um pacote por usar costuma significar não renovar. Reforçar a comunicação com o cliente.` }
            : { nivel:'reforcar', titulo:'Adesão abaixo do previsto',
                texto:`Só ${Math.round(ritmoRel*100)}% do consumo previsto. O programa está subutilizado: vale a pena reforçar a divulgação interna.` };
        } else if (ritmoRel < 0.8) {
          aviso = { nivel:'divulgar', titulo:'Abaixo do esperado',
            texto:`Cerca de ${Math.round(ritmoRel*100)}% do esperado a esta altura. Sem gravidade, mas uma ação de comunicação ajudava a recuperar.` };
        } else if (ritmoRel <= 1.2) {
          aviso = { nivel:'ok', titulo:'No ritmo',
            texto:'O consumo está em linha com o esperado. Só acompanhar.' };
        } else if (pacote) {
          aviso = { nivel:'vigiar', titulo:'Acima do ritmo',
            texto:`${Math.round(ritmoRel*100)}% do esperado. A este passo o pacote esgota antes do fim do contrato${mesesAteEsgotar!=null?` — cerca de ${mesesAteEsgotar} meses`:''}. Falar com o cliente sobre reforço antes de bloquear.` };
        } else {
          aviso = { nivel: ritmoRel > 1.5 ? 'vigiar' : 'atencao', titulo:'Acima do previsto',
            texto:`${Math.round(ritmoRel*100)}% do consumo previsto. A margem aperta se continuar${margemPrevista!=null?`; a previsão dava ${eur(margemPrevista)}`:''}. Vigiar o custo mensal.` };
        }
      }

      // o que a previsão custa
      const custoPrevisto     = custoPrevistoTmp;
      const custoPrevistoPior = (previstas && alvoPior) ? previstas*alvoPior : null;

      return { p, bs, pacote, consumidas, restam, ritmo, mesesAteEsgotar, envelope, envelopePior,
               previstas, doMes: doMes.length, nomeRef, agendadasMes, custoMes, custoMedio, alvoMedio, alvoPior, uni, sessAno,
               custoPrevisto, custoPrevistoPior, margemPrevista, aven,
               pctTempo, esperado, ritmoRel, aviso };
    }).filter(Boolean);
  }, [projsCli, sessCli, bolsas, briefings, ano, periodo]);

  // ── FATURADO REAL, CUSTO E MARGEM ──
  const norm = (s)=> (s||'').toString().normalize('NFKD').replace(/[\u0300-\u036f]/g,'')
    .toLowerCase().replace(/[^a-z0-9]/g,'');

  const fatCli = useMemo(() => {
    const cl = clientes.find(c=>c.id===clienteId);
    if(!cl) return [];
    const alvo = norm(cl.nome);
    // A fatura sai muitas vezes com o nome da empresa que paga e não com o do
    // cliente — a "Kagome Foods Portugal" é entidade da Hit Tomato.
    const designacoes = entidades.filter(e => e.cliente_id === clienteId)
      .map(e => norm(e.designacao_fiscal)).filter(Boolean);
    return faturas.filter(f => {
      if((f.estado||'')==='Anulada') return false;
      const n = norm(f.cliente);
      if(n===alvo || n.startsWith(alvo) || alvo.startsWith(n)) return true;
      return designacoes.some(d => n===d || n.startsWith(d) || d.startsWith(n));
    });
  }, [faturas, clientes, clienteId, entidades]);

  const liquidoDe = (f) => {
    const bruto = Number(f.valor)||0;
    const liq = Number(f.valor_liquido!=null ? f.valor_liquido : bruto/1.23)||0;
    const nc = Number(f.nc_valor)||0;
    return nc && bruto ? Math.max(0, liq - nc*(liq/bruto)) : liq;
  };
  const mesDe = (f) => (f.month || f.data_emissao || '').slice(0,7);

  const custosCli = useMemo(() =>
    custosDir.filter(x => x.cliente_id===clienteId || projIds.has(x.projeto_id)),
    [custosDir, clienteId, projIds]);

  // por mês: reconhecida, faturado, custo
  const porMes = useMemo(() => {
    return MESES.map((nm,i) => {
      const ym = ano+'-'+String(i+1).padStart(2,'0');
      const reconhecida = linhas[i]?.val || 0;
      const faturado = fatCli.filter(f=>mesDe(f)===ym).reduce((a,f)=>a+liquidoDe(f),0);
      const custoSess = sessCli.filter(s=>(s.date||'').slice(0,7)===ym)
                               .reduce((a,s)=>a+(Number(s.value_to_pay)||0),0);
      const custoDir  = custosCli.filter(x=>x.mes===ym).reduce((a,x)=>a+(Number(x.valor)||0),0);
      return { mes:nm, reconhecida, faturado, custo: custoSess+custoDir };
    });
  }, [linhas, fatCli, sessCli, custosCli, ano]);

  const maxMes = Math.max(1, ...porMes.flatMap(l=>[l.reconhecida,l.faturado,l.custo]));

  const noPeriodo = (i) => (i+1)>=periodo.de && (i+1)<=periodo.ate;
  const totFaturado = porMes.filter((_,i)=>noPeriodo(i)).reduce((a,m)=>a+m.faturado,0);
  const totCusto    = porMes.filter((_,i)=>noPeriodo(i)).reduce((a,m)=>a+m.custo,0);
  const totRecon    = porMes.filter((_,i)=>noPeriodo(i)).reduce((a,m)=>a+m.reconhecida,0);
  const margem      = totRecon - totCusto;
  const margemPct   = totRecon>0 ? Math.round(margem/totRecon*100) : null;

  // ── MIX E FALTAS ──
  // O presencial custa cerca do dobro do online, por isso o mix pesa na margem.
  // As faltas e os cancelamentos tardios pagam-se na mesma — são custo sem serviço.
  const qualidade = useMemo(() => {
    const noPer = s => { const m = Number((s.date||'').slice(5,7)); return m>=periodo.de && m<=periodo.ate; };
    // só as CONSULTAS (vêm da WOW): ginástica laboral, massagens e outras sessões de grupo lançadas no calendário
    // também têm «presencial» e baralhavam o mix (ex.: Banco BPI, 6 GL do Mês da Saúde contadas como consultas presenciais)
    const todas = sessCli.filter(noPer).filter(s => String(s.source || '').startsWith('wow') && (s.estado_sessao || s.modalidade));
    const agendadas = todas.filter(s => s.estado_sessao === 'agendada');
    const canceladas = todas.filter(s => String(s.estado_sessao||'').startsWith('cancelada') || s.estado_sessao === 'reagendada');
    const ss = todas.filter(s => !['agendada','reagendada'].includes(s.estado_sessao)
                              && !String(s.estado_sessao||'').startsWith('cancelada'));
    const comEstado = ss.filter(s=>s.estado_sessao);
    const realizadas = comEstado.filter(s=>s.estado_sessao==='realizada').length;
    const faltas     = comEstado.filter(s=>s.estado_sessao==='falta').length;
    const tardios    = comEstado.filter(s=>s.estado_sessao==='cancelamento_tardio').length;
    const comModal   = ss.filter(s=>s.modalidade);
    const presencial = comModal.filter(s=>s.modalidade==='presencial').length;
    const custoFaltas = comEstado.filter(s=>s.estado_sessao!=='realizada')
                                 .reduce((a,s)=>a+(Number(s.value_to_pay)||0),0);
    if (!ss.length && !agendadas.length && !canceladas.length) return null;
    const canceladasUtente = canceladas.filter(s=>s.estado_sessao==='cancelada_utente').length;
    const marcadas = ss.length + agendadas.length + canceladas.length;
    const futuras = agendadas.filter(s => (s.date||'') >= new Date().toISOString().slice(0,10)).length;
    return {
      total: ss.length, realizadas, faltas, tardios,
      agendadas: agendadas.length, futuras,
      canceladas: canceladas.length, canceladasUtente, marcadas,
      pctCancel: marcadas ? Math.round(canceladas.length/marcadas*100) : null,
      pctFaltas:     comEstado.length ? Math.round((faltas+tardios)/comEstado.length*100) : null,
      pctPresencial: comModal.length  ? Math.round(presencial/comModal.length*100) : null,
      presencial, online: comModal.length-presencial, custoFaltas,
      semClassificar: sessCli.filter(noPer).length - ss.length,
    };
  }, [sessCli, periodo]);

  const ativos = projsCli.filter(p=>p.status==='Ativo').length;
  const overdue = useMemo(()=> fases.filter(f=>projIds.has(f.project_id) && f.end_date && f.end_date < new Date().toISOString().slice(0,10) && !['Concluída','Concluido','Concluído','Done','Fechado'].includes(f.state)), [fases,projIds]);

  const donut=(data,total,size=150)=>{ const r=size/2-12,cx=size/2,cy=size/2,circ=2*Math.PI*r; let off=0;
    return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}><g transform={`rotate(-90 ${cx} ${cy})`}>
      {total>0?data.map((d,i)=>{const len=(d.value/total)*circ;const seg=<circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={d.color} strokeWidth={tip&&tip.k==='d'+i?22:18} strokeDasharray={`${len} ${circ-len}`} strokeDashoffset={-off} style={{cursor:'pointer'}} onMouseEnter={e=>setTip({k:'d'+i,x:e.clientX,y:e.clientY,label:d.label,val:d.value,pct:total>0?Math.round(d.value/total*100):0})} onMouseMove={e=>setTip(t=>t?{...t,x:e.clientX,y:e.clientY}:t)} onMouseLeave={()=>setTip(null)}/>;off+=len;return seg;}):<circle cx={cx} cy={cy} r={r} fill="none" stroke={C.border} strokeWidth="18"/>}
    </g></svg>; };

  const card=(label,val,cor,sub)=>(<div style={{ background:C.white, borderRadius:12, border:'1px solid '+C.border, padding:'16px 18px', flex:1, minWidth:160 }}>
    <div style={{ fontSize:12, color:C.soft, fontWeight:700, textTransform:'uppercase', letterSpacing:'.04em' }}>{label}</div>
    <div style={{ fontSize:25, fontWeight:800, color:cor||C.purple, margin:'4px 0 2px' }}>{val}</div>
    {sub!=null&&<div style={{ fontSize:12, color:C.soft }}>{sub}</div>}</div>);
  const painel={ background:C.white, borderRadius:12, border:'1px solid '+C.border, padding:'16px 18px', flex:1, minWidth:300 };
  const tit={ fontSize:14, fontWeight:800, color:C.txt, margin:'0 0 12px' };

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif", color:C.txt, padding:'24px 32px', boxSizing:'border-box', width:'100%' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={voltar} style={{ background:C.purpleLight, color:C.purple, border:'1px solid '+C.purple, borderRadius:8, padding:'7px 13px', fontSize:13, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>← Voltar</button>
          <h2 style={{ margin:0, fontSize:20, color:C.purple }}>🏢 Vista Cliente</h2>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <select value={clienteId} onChange={e=>setClienteId(e.target.value)} style={{ padding:'8px 12px', borderRadius:8, border:'1px solid '+C.purpleLight, fontSize:13, fontFamily:"'DM Sans',sans-serif", color:C.txt, minWidth:200 }}>
            <option value="">Escolher cliente…</option>
            {clientes.map(c=><option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <select value={ano} onChange={e=>setAno(parseInt(e.target.value))} style={{ padding:'8px 12px', borderRadius:8, border:'1px solid '+C.purpleLight, fontSize:13, fontFamily:"'DM Sans',sans-serif", color:C.txt }}>
            {[anoAtual-1,anoAtual,anoAtual+1].map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {clienteId && !loading && <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:16, flexWrap:'wrap' }}>
        {[['Ano',1,12],['Trimestre',Math.max(1,mesHoje-2),mesHoje],['Mês',mesHoje,mesHoje]].map(([lbl,d,a])=>{
          const ativo = periodo.de===d && periodo.ate===a;
          return <button key={lbl} onClick={()=>setPeriodo({de:d,ate:a})} style={{ padding:'6px 14px', borderRadius:8, border:'1px solid '+C.purpleLight, fontSize:13, fontFamily:"'DM Sans',sans-serif", cursor:'pointer', background:ativo?C.purple:'#fff', color:ativo?'#fff':C.txt, fontWeight:ativo?600:400 }}>{lbl}</button>;
        })}
        <span style={{ fontSize:13, color:C.soft, marginLeft:6 }}>De</span>
        <select value={periodo.de} onChange={e=>{ const d=parseInt(e.target.value); setPeriodo(p=>({de:d,ate:Math.max(d,p.ate)})); }} style={{ padding:'6px 10px', borderRadius:8, border:'1px solid '+C.purpleLight, fontSize:13, fontFamily:"'DM Sans',sans-serif", color:C.txt }}>
          {MESES.map((nm,i)=><option key={i} value={i+1}>{nm}</option>)}
        </select>
        <span style={{ fontSize:13, color:C.soft }}>até</span>
        <select value={periodo.ate} onChange={e=>{ const a=parseInt(e.target.value); setPeriodo(p=>({de:Math.min(a,p.de),ate:a})); }} style={{ padding:'6px 10px', borderRadius:8, border:'1px solid '+C.purpleLight, fontSize:13, fontFamily:"'DM Sans',sans-serif", color:C.txt }}>
          {MESES.map((nm,i)=><option key={i} value={i+1}>{nm}</option>)}
        </select>
      </div>}

      {loading ? <p style={{ color:C.soft }}>A carregar…</p> :
       !clienteId ? <div style={{ ...painel, textAlign:'center', color:C.soft, padding:'50px 20px' }}>Escolhe um cliente para ver o retrato completo.</div> : <>
        <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
          {card('Faturação reconhecida', eur(totalAno), C.purple, ano)}
          {card('Projetos ativos', ativos, C.purple, projsCli.length+' no total')}
          <div style={{ background:C.white, borderRadius:12, border:'1px solid '+(npsUltimo?'#27AE60':C.border), padding:'16px 18px', flex:1, minWidth:200 }}>
            <div style={{ fontSize:12, color:C.soft, marginBottom:6 }}>NPS RH {npsUltimo?`· ${labelSem(npsUltimo.semestre)}`:''}</div>
            <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
              <div style={{ fontSize:24, fontWeight:800, color:npsUltimo?(npsUltimo.valor>=9?'#27AE60':npsUltimo.valor>=7?'#F39C12':'#E74C3C'):C.soft, fontFamily:"'Fraunces',serif" }}>
                {npsUltimo?`${npsUltimo.valor}/10`:'—'}{npsAnterior&&npsUltimo?(npsUltimo.valor>npsAnterior.valor?' ↑':npsUltimo.valor<npsAnterior.valor?' ↓':' =' ):''}
              </div>
              {npsUltimo && <span style={{ fontSize:11, fontWeight:700, color:npsUltimo.valor>=9?'#27AE60':npsUltimo.valor>=7?'#F39C12':'#E74C3C' }}>{npsClassif(npsUltimo.valor)}</span>}
            </div>
            {npsCli.length>0 ? (
              <div style={{ fontSize:11, color:C.soft, marginTop:6, display:'flex', gap:8, flexWrap:'wrap' }}>
                {npsCli.slice(0,4).map(h=>(
                  <span key={h.semestre}>{labelSem(h.semestre)}: <strong style={{ color:'#1A1A2E' }}>{h.valor}</strong></span>
                ))}
              </div>
            ) : <div style={{ fontSize:11, color:C.soft, marginTop:4 }}>Sem avaliações ainda</div>}
          </div>
          {card('Faturado', eur(totFaturado), C.green, fatCli.length+' faturas')}
          {card('Custo', eur(totCusto), C.pink, 'profissionais e custos diretos')}
          {card('Margem', (margemPct!=null?margemPct+'%':'—'), margem>=0?C.green:C.red, eur(margem))}
          {card('Sessões no ano', sessCli.length, C.blue, null)}
          {card('Tarefas overdue', overdue.length, overdue.length>0?C.red:C.green, overdue.length>0?'precisam de atenção':'tudo em dia')}
        </div>

        {qualidade && (
          <div style={{ ...painel, marginBottom:16 }}>
            <div style={tit}>Mix e faltas <span style={{ fontWeight:400, fontSize:11, color:C.soft, textTransform:"none", letterSpacing:0 }}>· consultas (WOW)</span></div>
            <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
              {[
                { lbl:'Presencial', val: qualidade.pctPresencial!=null ? qualidade.pctPresencial+'%' : '—',
                  sub: `${qualidade.presencial} de ${qualidade.presencial+qualidade.online} sessões`,
                  cor: qualidade.pctPresencial==null ? C.soft : qualidade.pctPresencial>40 ? C.red : qualidade.pctPresencial>20 ? C.amber : C.green,
                  nota: 'O presencial custa cerca do dobro do online.' },
                { lbl:'Faltas e cancelamentos tardios', val: qualidade.pctFaltas!=null ? qualidade.pctFaltas+'%' : '—',
                  sub: `${qualidade.faltas} faltas · ${qualidade.tardios} tardios`,
                  cor: qualidade.pctFaltas==null ? C.soft : qualidade.pctFaltas>15 ? C.red : qualidade.pctFaltas>10 ? C.amber : C.green,
                  nota: qualidade.custoFaltas>0 ? `${eur(qualidade.custoFaltas)} pagos sem sessão dada.` : null },
                { lbl:'Realizadas', val: qualidade.realizadas, sub:`de ${qualidade.total} no período`, cor:C.purple, nota:null },
                { lbl:'Agendadas', val: qualidade.agendadas, sub: qualidade.futuras+' ainda por dar',
                  cor: C.blue, nota: qualidade.agendadas===0 ? 'Nada marcado para a frente.' : null },
                { lbl:'Canceladas a tempo', val: qualidade.pctCancel!=null ? qualidade.pctCancel+'%' : '—',
                  sub: `${qualidade.canceladas} de ${qualidade.marcadas} marcações`,
                  cor: qualidade.pctCancel==null ? C.soft : qualidade.pctCancel>25 ? C.red : qualidade.pctCancel>15 ? C.amber : C.green,
                  nota: qualidade.canceladasUtente ? `${qualidade.canceladasUtente} canceladas pelo colaborador. Não se pagam, mas ocupam agenda.` : null },
              ].map((k,i)=>(
                <div key={i} style={{ flex:1, minWidth:190, background:'#FBFAFF', border:'1px solid '+C.border, borderRadius:10, padding:'13px 15px' }}>
                  <div style={{ fontSize:11.5, color:C.soft, marginBottom:5, minHeight:30 }}>{k.lbl}</div>
                  <div style={{ fontSize:23, fontWeight:800, color:k.cor, fontFamily:"'Fraunces',serif" }}>{k.val}</div>
                  <div style={{ fontSize:11, color:C.soft, marginTop:3 }}>{k.sub}</div>
                  {k.nota && <div style={{ fontSize:10.5, color:C.soft, marginTop:5, lineHeight:1.45 }}>{k.nota}</div>}
                </div>
              ))}
            </div>
            {qualidade.semClassificar>0 && (
              <div style={{ fontSize:11, color:C.soft, marginTop:9 }}>
                {qualidade.semClassificar} sessões sem estado ou modalidade — são anteriores a este registo ou foram criadas à mão no calendário.
              </div>
            )}
          </div>
        )}

        {avalUlt && (
          <div style={{ ...painel, marginBottom:16 }}>
            <div style={tit}>Avaliação dos RH · {labelSem(avalUlt.semestre)}</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:12, marginTop:10 }}>
              {ESCALAS.map(e=>{
                const v = avalUlt[e.key];
                const va = avalAnt ? avalAnt[e.key] : null;
                const seta = (v!=null && va!=null) ? (v>va?' ↑':v<va?' ↓':' =') : '';
                const cor = v==null?C.soft:(v>=4?'#27AE60':v>=3?'#F39C12':'#E74C3C');
                return (
                  <div key={e.key} style={{ background:'#FBFAFF', border:`1px solid ${C.border}`, borderRadius:10, padding:'12px 14px' }}>
                    <div style={{ fontSize:11, color:C.soft, marginBottom:4, minHeight:28 }}>{e.label}</div>
                    <div style={{ fontSize:20, fontWeight:800, color:cor, fontFamily:"'Fraunces',serif" }}>{v!=null?`${v}/5`:'—'}{seta}</div>
                  </div>
                );
              })}
            </div>
            {avalUlt.p7_melhorar && (
              <div style={{ marginTop:14, padding:'12px 14px', background:'#FBFAFF', border:`1px solid ${C.border}`, borderRadius:10 }}>
                <div style={{ fontSize:11, color:C.soft, marginBottom:4 }}>O que devemos melhorar</div>
                <div style={{ fontSize:13, color:'#1A1A2E' }}>{avalUlt.p7_melhorar}</div>
              </div>
            )}
          </div>
        )}

        {avPart.length > 0 && (() => {
          const t = indicadores(avPart);
          const r1 = (v) => v == null || isNaN(v) ? '—' : (Math.round(v*10)/10).toLocaleString('pt-PT');
          const g = {}; avPart.forEach(r => { const k = r.servico_label || r.servico; (g[k] = g[k] || []).push(r); });
          const porServ = Object.entries(g).map(([k, xs]) => ({ k, ...indicadores(xs) })).sort((a,b) => b.n - a.n);
          const coments = avPart.filter(r => r.valorizou || r.melhorar).slice(0, 6);
          const corSat = (v) => v == null ? C.soft : v >= 4 ? '#27AE60' : v >= 3 ? '#F39C12' : '#E74C3C';
          return (
          <div style={{ ...painel, marginBottom:16 }}>
            <div style={tit}>Avaliação dos participantes · últimos 12 meses · {t.n} resposta{t.n===1?'':'s'}</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:12, marginBottom:12 }}>
              {[['Satisfação', t.sat == null ? '—' : r1(t.sat) + '/5', corSat(t.sat)], ['NPS', t.nps ?? '—', C.purple],
                ['Antes → depois', t.delta == null ? '—' : (t.delta > 0 ? '+' : '') + r1(t.delta), C.purple], ['Aplica no dia a dia', t.aplicar == null ? '—' : t.aplicar + '%', C.purple]].map(([l, v, cor]) => (
                <div key={l} style={{ background:'#FBFAFF', border:`1px solid ${C.border}`, borderRadius:10, padding:'12px 14px' }}>
                  <div style={{ fontSize:11, color:C.soft, marginBottom:4 }}>{l}</div>
                  <div style={{ fontSize:20, fontWeight:800, color:cor, fontFamily:"'Fraunces',serif" }}>{v}</div>
                </div>))}
            </div>
            <details>
            <summary style={{ fontSize:12, color:C.purple, cursor:'pointer', fontWeight:700, marginBottom:6 }}>Ver por serviço e comentários</summary>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
              <thead><tr style={{ color:C.soft, textAlign:'left' }}><th style={{ padding:'4px 0' }}>Serviço</th><th>Respostas</th><th>Satisfação</th><th>NPS</th><th>Antes → depois</th><th>Aplica</th></tr></thead>
              <tbody>{porServ.map(x => (
                <tr key={x.k} style={{ borderTop:`1px solid ${C.border}` }}>
                  <td style={{ padding:'5px 0', fontWeight:600 }}>{x.k}</td><td>{x.n}</td><td style={{ color:corSat(x.sat) }}>{r1(x.sat)}</td><td>{x.nps ?? '—'}</td>
                  <td>{x.delta == null ? '—' : (x.delta > 0 ? '+' : '') + r1(x.delta)}</td><td>{x.aplicar == null ? '—' : x.aplicar + '%'}</td>
                </tr>))}</tbody>
            </table>
            {coments.length > 0 && (<div style={{ marginTop:12 }}>
              <div style={{ fontSize:11, color:C.soft, marginBottom:6 }}>Últimos comentários</div>
              {coments.map(r => (
                <div key={r.id} style={{ borderLeft:`3px solid ${C.purpleLight}`, padding:'3px 10px', marginBottom:6, fontSize:12.5 }}>
                  <div style={{ fontSize:11, color:C.soft }}>{r.servico_label}{r.profissional ? ' · ' + r.profissional : ''} · {String(r.sessao_data||'').split('-').reverse().join('/')}
                    {r.comentarios_estado === 'pendente' && <span style={{ color:'#F39C12' }}> · por aprovar</span>}{r.comentarios_estado === 'rejeitado' && <span style={{ color:'#E74C3C' }}> · não vai ao portal</span>}</div>
                  {r.valorizou && <div>👍 {r.valorizou}</div>}{r.melhorar && <div>💡 {r.melhorar}</div>}
                </div>))}
            </div>)}
            <div style={{ fontSize:11, color:C.soft, marginTop:8 }}>Detalhe por profissional e por mês em Operações → Avaliação dos serviços.</div>
            </details>
          </div>); })()}

        <div style={{ ...painel, marginBottom:16 }}>
          <div style={tit}>Evolução mensal · reconhecida, faturado e custo</div>
          <div style={{ display:'flex', gap:14, fontSize:11.5, color:C.soft, marginBottom:6, flexWrap:'wrap' }}>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:C.purple }}/>Reconhecida</span>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:'#27AE60' }}/>Faturado</span>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:2, background:C.pink }}/>Custo</span>
          </div>
          <svg viewBox="0 0 720 210" style={{ width:'100%', height:'auto', maxHeight:150 }}>
            {[0,0.5,1].map((g,gi)=>{ const y=180-g*160; return <g key={gi}><line x1="0" y1={y} x2="720" y2={y} stroke={C.border}/><text x="0" y={y-3} fontSize="9" fill={C.soft}>{eur(maxMes*g)}</text></g>; })}
            {porMes.map((l,i)=>{
              const x=i*60+8, esc=(v)=>(maxMes>0?(v/maxMes)*160:0), op=noPeriodo(i)?1:0.3;
              const barras=[['reconhecida',C.purple],['faturado','#27AE60'],['custo',C.pink]];
              return <g key={i}>
                {barras.map(([k,cor],bi)=>{ const h=esc(l[k]); if(h<=0) return null; return (
                  <rect key={k} x={x+bi*15} y={180-h} width={13} height={h} fill={cor} rx="2" opacity={op} style={{cursor:'pointer'}}
                    onMouseEnter={e=>setTip({k:'b'+i+k,x:e.clientX,y:e.clientY,label:l.mes+' · '+k,val:l[k]})}
                    onMouseMove={e=>setTip(t=>t?{...t,x:e.clientX,y:e.clientY}:t)}
                    onMouseLeave={()=>setTip(null)}/> ); })}
                <text x={x+22} y={196} fontSize="10" fill={C.soft} textAnchor="middle">{l.mes}</text>
              </g>; })}
          </svg>
        </div>

        <div style={{ display:'flex', gap:16, marginBottom:16, flexWrap:'wrap' }}>
          <div style={painel}>
            <div style={tit}>Faturação por área</div>
            <div style={{ display:'flex', justifyContent:'center' }}>{donut(porArea,totArea)}</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginTop:10, justifyContent:'center' }}>
              {porArea.map((d,i)=><span key={i} style={{ display:'flex', alignItems:'center', gap:5, fontSize:12 }}><span style={{ width:10, height:10, borderRadius:3, background:d.color }}/>{LINHA_ICON[d.label]||''} {d.label} · {totArea>0?Math.round(d.value/totArea*100):0}%</span>)}
              {porArea.length===0 && <span style={{ color:C.soft, fontSize:13 }}>Sem faturação este ano.</span>}
            </div>
          </div>
          <div style={painel}>
            <div style={tit}>Rentabilidade</div>
            {[['Receita reconhecida', totRecon, C.purple, 'o que o contrato vale no período'],
              ['Faturado', totFaturado, '#27AE60', fatCli.length+' faturas emitidas'],
              ['Custo dos profissionais', sessCli.filter((_,k)=>true).reduce((a,s)=>a+(Number(s.value_to_pay)||0),0), C.pink, sessCli.length+' sessões'],
              ['Custos diretos', custosCli.reduce((a,x)=>a+(Number(x.valor)||0),0), C.pink, custosCli.length+' lançamentos'],
            ].map(([lbl,val,cor,sub],i)=>(
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:10,
                padding:'9px 0', borderTop: i>0?'1px solid '+C.border:'none' }}>
                <div>
                  <div style={{ fontSize:13, color:'#1A1A2E' }}>{lbl}</div>
                  <div style={{ fontSize:11, color:C.soft }}>{sub}</div>
                </div>
                <strong style={{ fontSize:15, color:cor, whiteSpace:'nowrap' }}>{eur(val)}</strong>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:10,
              padding:'11px 0 2px', borderTop:'2px solid '+C.border, marginTop:4 }}>
              <span style={{ fontSize:13.5, fontWeight:700 }}>Margem</span>
              <span style={{ whiteSpace:'nowrap' }}>
                <strong style={{ fontSize:18, color: margem>=0?'#27AE60':C.red, fontFamily:"'Fraunces',serif" }}>{eur(margem)}</strong>
                {margemPct!=null && <span style={{ fontSize:13, color:C.soft, marginLeft:7 }}>{margemPct}%</span>}
              </span>
            </div>
            <div style={{ fontSize:10.5, color:C.soft, marginTop:8, lineHeight:1.5 }}>
              A margem compara a receita reconhecida com os custos. O faturado pode diferir
              da reconhecida quando se fatura adiantado ou em atraso.
            </div>
          </div>
        </div>

        {consumo.length>0 &&
        <div style={{ ...painel, marginBottom:16 }}>
          <div style={tit}>Consumo e envelope</div>
          {consumo.map((x,i)=>{
            const pct = x.pacote ? Math.round(x.consumidas/x.pacote*100)
                      : x.envelope ? Math.round(x.doMes/x.envelope*100) : null;
            const cor = pct==null ? C.soft : pct>=100 ? C.red : pct>=80 ? '#C9A227' : C.green;
            return (
              <div key={i} style={{ padding:'12px 0', borderTop: i>0?'1px solid '+C.border:'none' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:10, flexWrap:'wrap', marginBottom:7 }}>
                  <span style={{ fontSize:13.5, fontWeight:700, color:C.purple }}>{x.p.name}</span>
                  <span style={{ fontSize:12, color:C.soft }}>{x.p.billing_model}</span>
                </div>

                <div style={{ fontSize:11.5, color:C.soft, marginBottom:7 }}>
                  {x.bs.map(b=>`${b.nome}: ${b.sessoes_ano ? b.sessoes_ano+'/ano' : b.total_pacote ? b.total_pacote+' no total' : 'sem limite'}`).join(' · ')}
                </div>
                {x.pacote>0 ? (
                  <>
                    <div style={{ fontSize:13, color:C.txt, marginBottom:6 }}>
                      <strong>{x.consumidas}</strong> de {x.pacote} consultas
                      <span style={{ color:C.soft }}> · restam {x.restam}</span>
                      {pct!=null && <strong style={{ color:cor, marginLeft:8 }}>{pct}%</strong>}
                    </div>
                    <div style={{ height:9, background:C.border, borderRadius:5, overflow:'hidden' }}>
                      <div style={{ height:9, width:Math.min(100,pct||0)+'%', background:cor }}/>
                    </div>
                    <div style={{ fontSize:11.5, color:C.soft, marginTop:6 }}>
                      Ritmo de {x.ritmo.toFixed(1)} consultas/mês
                      {x.mesesAteEsgotar!=null && x.restam>0 && <> · esgota em cerca de <strong>{x.mesesAteEsgotar} meses</strong> a este ritmo</>}
                      {x.restam===0 && <strong style={{ color:C.red }}> · pacote esgotado</strong>}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display:'flex', gap:18, flexWrap:'wrap', fontSize:13, marginBottom:7 }}>
                      <span>{x.nomeRef ? x.nomeRef.charAt(0).toUpperCase() + x.nomeRef.slice(1) : 'Este mês'}: <strong>{x.doMes}</strong> sessões
                        {x.agendadasMes>0 && <span style={{ color:C.soft }}> ({x.agendadasMes} por dar)</span>}
                        {x.custoMes>0 && <span style={{ color:C.soft }}> · {eur(x.custoMes)}</span>}</span>
                      {x.envelope!=null && <span style={{ color:C.soft }}>Envelope: <strong style={{ color:C.txt }}>{x.envelope}</strong>/mês</span>}
                      {x.previstas!=null && <span style={{ color:C.soft }}>Teto: <strong style={{ color:C.txt }}>{x.previstas}</strong>/mês</span>}
                      {pct!=null && <strong style={{ color:cor }}>{pct}% do envelope</strong>}
                    </div>
                    {x.envelope!=null &&
                      <div style={{ height:9, background:C.border, borderRadius:5, overflow:'hidden' }}>
                        <div style={{ height:9, width:Math.min(100,pct||0)+'%', background:cor }}/>
                      </div>}
                    {x.custoPrevisto!=null && (
                      <div style={{ background:'#FAF8FF', borderRadius:8, padding:'10px 13px', marginTop:9, fontSize:12.5, lineHeight:1.65 }}>
                        <div>
                          Previsão de <strong>{x.previstas}</strong> sessões/mês a {x.alvoMedio.toFixed(2)}€
                          {' = '}<strong style={{ color:C.pink }}>{eur(x.custoPrevisto)}</strong> de custo
                          {x.custoPrevistoPior!=null && x.custoPrevistoPior!==x.custoPrevisto &&
                            <span style={{ color:C.soft }}> · até {eur(x.custoPrevistoPior)} no pior caso</span>}
                        </div>
                        {x.margemPrevista!=null && (
                          <div style={{ marginTop:3 }}>
                            Contra uma avença de {eur(x.aven)}, sobra uma margem prevista de{' '}
                            <strong style={{ color: x.margemPrevista>=0 ? '#27AE60' : C.red }}>{eur(x.margemPrevista)}</strong>
                            {x.aven>0 && <span style={{ color:C.soft }}> ({Math.round(x.margemPrevista/x.aven*100)}%)</span>}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize:11.5, color:C.soft, marginTop:6 }}>
                      {x.custoMedio!=null && <>Custo médio de {x.custoMedio.toFixed(2)}€ por sessão
                        {x.alvoMedio && <span style={{ color: x.custoMedio>x.alvoMedio ? C.red : C.green, fontWeight:700 }}>
                          {' '}({x.custoMedio>x.alvoMedio ? 'acima' : 'dentro'} do alvo de {x.alvoMedio.toFixed(2)}€)</span>} · </>}
                      {x.envelopePior!=null && x.envelopePior!==x.envelope && <>no pior caso — se todos escolherem a valência mais cara — o envelope desce para <strong>{x.envelopePior}</strong> · </>}
                      {x.previstas!=null && x.envelope!=null && (x.previstas>x.envelope
                        ? <strong style={{ color:C.red }}>o teto excede o envelope em {x.previstas-x.envelope} sessões/mês</strong>
                        : <span>o teto cabe no envelope</span>)}
                    </div>
                  </>
                )}

                {x.aviso && (() => {
                  const est = {
                    travar:   { cor:'#C0392B', fundo:'#FDECEA', sinal:'Travar' },
                    parado:   { cor:'#C0392B', fundo:'#FDECEA', sinal:'Parado' },
                    vigiar:   { cor:'#B9770E', fundo:'#FDF8E7', sinal:'Vigiar' },
                    atencao:  { cor:'#B9770E', fundo:'#FDF8E7', sinal:'Atenção' },
                    divulgar: { cor:'#B9770E', fundo:'#FDF8E7', sinal:'Divulgar' },
                    reforcar: { cor:'#1F6FB2', fundo:'#EAF4FB', sinal:'Reforçar adesão' },
                    ok:       { cor:'#1E8449', fundo:'#EAFAF1', sinal:'No ritmo' },
                  }[x.aviso.nivel] || { cor:C.soft, fundo:'#F7F7FA', sinal:'—' };
                  return (
                    <div style={{ background:est.fundo, borderRadius:8, padding:'10px 13px', marginTop:9 }}>
                      <div style={{ display:'flex', gap:8, alignItems:'baseline', flexWrap:'wrap', marginBottom:3 }}>
                        <span style={{ fontSize:10.5, fontWeight:700, color:'#fff', background:est.cor, borderRadius:10, padding:'2px 9px' }}>{est.sinal}</span>
                        <strong style={{ fontSize:12.5, color:est.cor }}>{x.aviso.titulo}</strong>
                        {x.ritmoRel!=null && (
                          <span style={{ fontSize:11, color:C.soft, marginLeft:'auto' }}>
                            {x.consumidas} de {Math.round(x.esperado)} esperadas · {Math.round(x.pctTempo*100)}% do contrato decorrido
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize:12, color:'#1A1A2E', lineHeight:1.6 }}>{x.aviso.texto}</div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
          <div style={{ fontSize:11, color:C.soft, marginTop:10, lineHeight:1.55 }}>
            As sessões são contadas pelo cliente, não pelo projeto — num cliente com vários
            programas em curso, o número é do conjunto.
          </div>
        </div>}

        {overdue.length>0 &&
        <div style={painel}>
          <div style={tit}>⚠ Tarefas em atraso ({overdue.length})</div>
          {overdue.slice(0,12).map((f,i)=>{ const proj=projsCli.find(p=>p.id===f.project_id);
            return <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid '+C.border, fontSize:13 }}>
              <span>{f.name} <span style={{ color:C.soft }}>· {proj?.name||''}</span></span>
              <span style={{ color:C.red, fontWeight:600 }}>{f.end_date}</span>
            </div>; })}
        </div>}
      </>}
      {tip && <div style={{ position:'fixed', left:tip.x+12, top:tip.y-8, background:'#2b2b3a', color:'#fff', padding:'6px 10px', borderRadius:8, fontSize:12, fontWeight:600, pointerEvents:'none', zIndex:9999, whiteSpace:'nowrap', boxShadow:'0 4px 16px rgba(0,0,0,0.25)' }}>{tip.label}: {eur(tip.val)}{tip.pct!=null?' ('+tip.pct+'%)':''}</div>}
      {npsModal && <NpsModal clienteId={clienteId} clienteNome={(clientes.find(c=>c.id===clienteId)||{}).nome} historico={[]} onClose={()=>setNpsModal(false)} onSaved={()=>setNpsModal(false)}/>}
    </div>
  );
}

function NpsModal({ clienteId, clienteNome, historico=[], onClose, onSaved }){
  const [data,setData]=useState(()=>new Date().toISOString().slice(0,10));
  const [valor,setValor]=useState('');
  const [nota,setNota]=useState('');
  const [saving,setSaving]=useState(false);
  const guardar=async()=>{
    const v=parseInt(valor,10);
    if(isNaN(v)||v<-100||v>100){ alert('O NPS deve ser um número entre -100 e 100.'); return; }
    setSaving(true);
    const { error }=await supabase.from('nps_clientes').insert({ cliente_id:clienteId, data_medicao:data, valor:v, nota:nota||null });
    setSaving(false);
    if(error){ alert('Erro ao gravar: '+error.message); return; }
    onSaved();
  };
  const inp={ width:'100%', padding:'9px 11px', border:'1px solid #E4DEFA', borderRadius:7, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box' };
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(20,10,40,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:460, maxHeight:'85vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)', padding:'22px' }}>
        <div style={{ fontSize:11, fontWeight:700, color:'#1E8449', textTransform:'uppercase', letterSpacing:0.8 }}>Registar NPS RH</div>
        <div style={{ fontSize:17, fontWeight:700, color:'#691FB1', fontFamily:"'Fraunces',serif", marginBottom:16 }}>{clienteNome||''}</div>
        <div style={{ display:'flex', gap:10, marginBottom:12 }}>
          <div style={{ flex:1 }}><div style={{ fontSize:10, color:'#888', fontWeight:600, marginBottom:3 }}>DATA</div><input type="date" value={data} onChange={e=>setData(e.target.value)} style={inp}/></div>
          <div style={{ flex:1 }}><div style={{ fontSize:10, color:'#888', fontWeight:600, marginBottom:3 }}>NPS (-100 a 100)</div><input value={valor} onChange={e=>setValor(e.target.value)} placeholder="ex: 72" style={inp}/></div>
        </div>
        <div style={{ marginBottom:14 }}><div style={{ fontSize:10, color:'#888', fontWeight:600, marginBottom:3 }}>NOTA (opcional)</div><input value={nota} onChange={e=>setNota(e.target.value)} placeholder="contexto, quem respondeu…" style={inp}/></div>
        {historico.length>0 && <div style={{ marginBottom:14 }}><div style={{ fontSize:10, color:'#888', fontWeight:600, marginBottom:5 }}>HISTÓRICO</div>{historico.slice(0,5).map((h,i)=>(<div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'4px 0', borderBottom:'1px solid #F2EEFB' }}><span style={{ color:'#666' }}>{h.data_medicao}</span><span style={{ fontWeight:700, color:h.valor>=0?'#27AE60':'#E74C3C' }}>{h.valor}</span></div>))}</div>}
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:10, border:'1px solid #E4DEFA', borderRadius:8, background:'#fff', color:'#4A4A6A', fontSize:13, cursor:'pointer' }}>Cancelar</button>
          <button onClick={guardar} disabled={saving} style={{ flex:2, padding:10, border:'none', borderRadius:8, background:'#691FB1', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>{saving?'A gravar…':'Registar NPS'}</button>
        </div>
      </div>
    </div>
  );
}
